import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener('click', this.#handleRackColorClick, { signal });
    target?.addEventListener('change', this.#handleRackColorClick, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const target = this.closest('.shopify-section, dialog');

    if (this.hasAttribute('data-bundle-gallery')) {
      event.promise
        .then(({ detail }) => {
          const selectedMediaId = detail?.html?.querySelector('media-gallery')?.dataset.selectedMediaId;
          if (selectedMediaId) this.selectMediaById(selectedMediaId);
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
        });
      return;
    }

    // Rack media always stays in a fixed order, so replacing the entire gallery
    // only creates a visible flash back to the first (fan) image.
    if (this.dataset.mediaIds) {
      const selectedMediaId =
        document.documentElement.dataset.rackSelectedMediaId || target?.dataset.rackSelectedMediaId;
      if (selectedMediaId) this.selectMediaById(selectedMediaId);

      event.promise.catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
        const restoreRackMedia = () => {
          if (!newMediaGallery.isConnected) return;

          const selectedMediaId =
            document.documentElement.dataset.rackSelectedMediaId ||
            target?.dataset.rackSelectedMediaId ||
            newMediaGallery.dataset.selectedMediaId;
          if (selectedMediaId) newMediaGallery.selectMediaById(selectedMediaId);
        };

        requestAnimationFrame(() => {
          requestAnimationFrame(restoreRackMedia);
        });
        setTimeout(restoreRackMedia, 150);
        setTimeout(restoreRackMedia, 400);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #handleRackColorClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const input = event.target.closest('.md-rack-options__color input[data-preview-media-id]');
    if (!(input instanceof HTMLInputElement)) return;

    const mediaId = input.dataset.previewMediaId;
    if (!mediaId) return;

    const target = this.closest('.shopify-section, dialog');
    if (target instanceof HTMLElement) target.dataset.rackSelectedMediaId = mediaId;
    document.documentElement.dataset.rackSelectedMediaId = mediaId;

    this.selectMediaById(mediaId);
  };

  selectMediaById(mediaId) {
    const mediaIds = this.dataset.mediaIds?.split(',') ?? [];
    const index = mediaIds.indexOf(mediaId);
    if (index < 0) return;

    this.slideshow?.select(index, undefined, { animate: false });
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
